import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAmazonProfile } from '../contexts/AmazonProfileContext';
import { useTheme } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';
import api from '../services/api';
import {
  BuildingStorefrontIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import StoreRow from '../components/stores/StoreRow';
import { StoreListTableHeader, STORE_LIST_TABLE_MIN_W } from '../components/stores/storeListGrid';
import StoreConnectionBanner from '../components/stores/StoreConnectionBanner';
import TablePagination from '../components/ui/TablePagination';
import HubFilterSelect from '../components/ui/HubFilterSelect';
import useDebouncedValue from '../hooks/useDebouncedValue';
import useStorePerformanceSummaries from '../hooks/useStorePerformanceSummaries';
import { useTablePagination } from '../hooks/useTablePagination';
import {
  filterStores,
  filterStoreGroups,
  groupStoresByName,
  normalizeStoreList,
  partitionStoresByConnection,
} from '../utils/storeUtils';
import { sortStoreGroups, STORE_LIST_SORT_OPTIONS } from '../utils/storeListSort';
import usePortfolioDashboard from '../hooks/usePortfolioDashboard';
import { storeHubPeriodBounds } from '../utils/storeHubPeriodBounds';
import StoreHubPortfolioSummary from '../components/stores/StoreHubPortfolioSummary';
import StoreAttentionCenter from '../components/stores/StoreAttentionCenter';
import { buildAttentionIndex } from '../utils/storeAttention';
import QCommerceStoresHubList from '../components/stores/QCommerceStoresHubList';
import { useStoreHubChrome } from '../components/layout/StoreHubShell';

import { isQcPlatformId, isQuickCommerceHubFilter } from '../constants/qcommerce';
import {
  DEFAULT_STORE_HUB_REGION,
  listStoreRegionCodes,
  regionFilterLabel,
  storeMatchesRegionFilter,
} from '../utils/storeHubRegionFilter';

function StoreSelection() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { setQcStoreCount, qcStoreCount } = useStoreHubChrome();
  const {
    profiles,
    loading,
    selectProfile,
    refreshProfiles,
    profilesHasMore,
    loadMoreProfiles,
  } = useAmazonProfile();
  const { isDarkMode, toggleTheme } = useTheme();

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canConnect = user?.role === 'store_owner' || isAdmin;
  const canDisconnect = user?.role === 'store_owner' || isAdmin;

  const [hubSummary, setHubSummary] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 280);
  const [platformFilter, setPlatformFilter] = useState('amazon');
  const [connectionFilter, setConnectionFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState(DEFAULT_STORE_HUB_REGION);
  const [sortBy, setSortBy] = useState('spend_desc');
  const [attentionFilter, setAttentionFilter] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState(null);

  const hubPeriod = useMemo(() => storeHubPeriodBounds(), []);
  const { portfolio, loading: portfolioLoading } = usePortfolioDashboard(null, {
    ...hubPeriod,
    enabled: Boolean(user) && !isQuickCommerceHubFilter(platformFilter),
  });

  const isQcHubView = isQuickCommerceHubFilter(platformFilter);

  useEffect(() => {
    if (!user) return;
    api.get('/stores/summary').then((res) => {
      if (res.data?.success) setHubSummary(res.data.summary);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    const p = searchParams.get('platform');
    if (p === 'quick_commerce' || isQcPlatformId(p)) {
      setPlatformFilter('quick_commerce');
    } else {
      setPlatformFilter('amazon');
    }
  }, [searchParams]);

  const stores = useMemo(() => normalizeStoreList(profiles), [profiles]);

  const regionOptions = useMemo(() => listStoreRegionCodes(stores), [stores]);

  const showRegionHintInRows = regionFilter === 'all';

  const filteredGroups = useMemo(() => {
    let list = filterStores(stores, { query: debouncedSearch, platform: 'amazon' });
    if (regionFilter !== 'all') {
      list = list.filter((s) => storeMatchesRegionFilter(s, regionFilter));
    }
    if (connectionFilter === 'connected') {
      list = list.filter((s) => s.isConnected);
    } else if (connectionFilter === 'disconnected') {
      list = list.filter((s) => !s.isConnected);
    }

    if (regionFilter === 'all') {
      const flat = list.map((s) => ({
        id: String(s.storeId),
        name: s.name,
        stores: [s],
        hasMultipleRegions: false,
        primaryStore: s,
      }));
      return filterStoreGroups(flat, { query: debouncedSearch, platform: 'amazon' });
    }

    const grouped = groupStoresByName(list);
    return filterStoreGroups(grouped, { query: debouncedSearch, platform: 'amazon' });
  }, [stores, debouncedSearch, regionFilter, connectionFilter]);

  const metricsProfileIds = useMemo(
    () => [...new Set(
      filteredGroups.map((g) => g.primaryStore?.profileId || g.primaryStore?.storeId).filter(Boolean)
    )],
    [filteredGroups]
  );

  const { summaries: perfSummaries, bounds: perfBounds, loading: metricsLoading } = useStorePerformanceSummaries(
    metricsProfileIds,
    { enabled: metricsProfileIds.length > 0 }
  );

  const sortedGroups = useMemo(
    () => sortStoreGroups(filteredGroups, sortBy, perfSummaries),
    [filteredGroups, sortBy, perfSummaries]
  );

  const attention = useMemo(
    () => buildAttentionIndex(filteredGroups, perfSummaries),
    [filteredGroups, perfSummaries]
  );

  const visibleGroups = useMemo(() => {
    if (!attentionFilter) return sortedGroups;
    return sortedGroups.filter((g) => attention.byGroup.get(g.id)?.severity === attentionFilter);
  }, [sortedGroups, attentionFilter, attention]);

  const listPagination = useTablePagination(visibleGroups, [
    attentionFilter,
    debouncedSearch,
    platformFilter,
    regionFilter,
    connectionFilter,
    sortBy,
    perfSummaries,
  ], 12);

  const { connected: connectedCountList, disconnected: disconnectedCountList } = useMemo(() => {
    const partitioned = partitionStoresByConnection(
      filterStores(stores, { query: debouncedSearch, platform: 'amazon' })
    );
    return { connected: partitioned.connected.length, disconnected: partitioned.disconnected.length };
  }, [stores, debouncedSearch]);

  const amazonTotalCount = useMemo(
    () => groupStoresByName(stores).length,
    [stores]
  );

  const handleEnterStore = (store) => {
    selectProfile(store);
    localStorage.setItem('selectedAmazonProfile', JSON.stringify(store));
    if (!store.isConnected) {
      toast('Viewing cached data — reconnect to refresh from Amazon', { icon: 'ℹ️' });
    }
    navigate('/dashboard');
  };

  const handleConnectStore = async () => {
    const email = user?.email;
    if (!email) {
      toast.error('Sign in again to connect a store');
      return;
    }
    setConnecting(true);
    try {
      const clientUrl = encodeURIComponent(window.location.origin);
      const res = await api.get(
        `/onboarding/amazon/start?email=${encodeURIComponent(email)}&type=login&clientUrl=${clientUrl}`
      );
      if (res.data?.success && res.data.authUrl) {
        window.location.href = res.data.authUrl;
      } else {
        toast.error('Could not start store connection');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to connect store');
    } finally {
      setConnecting(false);
    }
  };

  const handleReconnect = () => handleConnectStore();

  const handleDisconnect = async (store) => {
    if (!canDisconnect) return;
    const region = store.region || store.countryCode || '';
    const label = region ? `${store.name} — ${region} (${store.currency || ''})` : store.name;
    const confirmed = window.confirm(
      `Disconnect marketplace "${label}"?\n\nOnly this country/profile is removed. Other marketplaces under the same seller name stay connected. Reconnect anytime via Connect store.`
    );
    if (!confirmed) return;

    const profileId = store.profileId || store.storeId;
    setDisconnectingId(profileId);
    try {
      const res = await api.post(`/stores/${profileId}/disconnect`);
      if (res.data?.success) {
        toast.success(res.data.message || 'Store disconnected');
        await refreshProfiles();
        const sumRes = await api.get('/stores/summary');
        if (sumRes.data?.success) setHubSummary(sumRes.data.summary);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to disconnect store');
    } finally {
      setDisconnectingId(null);
    }
  };

  const pageLoading = loading;
  const disconnectedList = useMemo(
    () => partitionStoresByConnection(stores).disconnected,
    [stores]
  );
  const totalCount = groupStoresByName(stores).length;

  if (pageLoading && !stores.length) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <header className={`px-6 py-5 border-b shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className={`text-2xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {isQcHubView ? 'Quick Commerce' : 'Amazon stores'}
              </h1>
              <p className={`text-sm mt-1.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                {isQcHubView
                  ? `${qcStoreCount ?? '…'} workspace${qcStoreCount === 1 ? '' : 's'} · Blinkit, Zepto, Instamart`
                  : hubSummary
                    ? `${hubSummary.connectedCount} connected · ${hubSummary.disconnectedCount} disconnected`
                    : `${connectedCountList} connected · ${disconnectedCountList} disconnected`}
              </p>
              {!isQcHubView ? (
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                {hubPeriod.label}
                {perfBounds?.label ? ` · ${perfBounds.label}` : ''}
              </p>
              ) : null}
              {canDisconnect && regionFilter !== 'all' && (
                <p className={`text-xs mt-1 max-w-xl ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                  Showing <strong>{regionFilterLabel(regionFilter)}</strong> only — change{' '}
                  <strong>Country</strong> above to manage other marketplaces. Disconnect removes that country only.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={toggleTheme}
              className={`p-2 rounded-lg border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-slate-50'}`}
              title="Toggle theme"
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
          </div>

          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-xl">
              <MagnifyingGlassIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, ID, country, or account…"
                className={`w-full pl-10 pr-4 py-2.5 text-sm rounded-lg border ${
                  isDarkMode
                    ? 'bg-slate-950 border-slate-700 text-white placeholder-slate-600'
                    : 'bg-slate-50 border-slate-300'
                }`}
              />
            </div>
            <div className="flex flex-wrap items-stretch gap-2.5 w-full lg:w-auto lg:shrink-0">
              <HubFilterSelect
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                isDarkMode={isDarkMode}
                aria-label="Country filter"
                className="min-w-[11rem] flex-1 sm:flex-none"
              >
                <option value="all">All countries</option>
                {regionOptions.map(({ code, count }) => (
                  <option key={code} value={code}>
                    {regionFilterLabel(code)}{count ? ` · ${count}` : ''}
                  </option>
                ))}
              </HubFilterSelect>
              <HubFilterSelect
                value={connectionFilter}
                onChange={(e) => setConnectionFilter(e.target.value)}
                isDarkMode={isDarkMode}
                aria-label="Connection filter"
                className="min-w-[11.5rem] flex-1 sm:flex-none"
              >
                <option value="all">All connections</option>
                <option value="connected">Connected only</option>
                <option value="disconnected">Disconnected only</option>
              </HubFilterSelect>
              <HubFilterSelect
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                isDarkMode={isDarkMode}
                aria-label="Sort stores"
                className="min-w-[12.5rem] flex-1 sm:flex-none"
              >
                {STORE_LIST_SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </HubFilterSelect>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {isQcHubView ? (
            <QCommerceStoresHubList
              isDarkMode={isDarkMode}
              searchQuery={debouncedSearch}
              onStoreCount={setQcStoreCount}
            />
          ) : (
          <>
          <StoreHubPortfolioSummary
            portfolio={portfolio}
            loading={portfolioLoading}
            isDarkMode={isDarkMode}
          />
          <StoreAttentionCenter
            attention={attention}
            loading={metricsLoading}
            activeFilter={attentionFilter}
            onFilterChange={setAttentionFilter}
            onOpenStore={handleEnterStore}
            isDarkMode={isDarkMode}
          />
          {(hubSummary?.disconnectedCount > 0 || disconnectedList.length > 0) && (
            <StoreConnectionBanner
              summary={hubSummary || {
                connectedCount: partitionStoresByConnection(stores).connected.length,
                disconnectedCount: disconnectedList.length,
              }}
              disconnectedStores={disconnectedList}
              isDarkMode={isDarkMode}
            />
          )}

          {stores.length === 0 ? (
            <EmptyState isDarkMode={isDarkMode} onConnect={handleConnectStore} connecting={connecting} canConnect={canConnect} />
          ) : visibleGroups.length === 0 ? (
            <div className={`text-center py-16 rounded-xl border ${isDarkMode ? 'border-slate-800 bg-slate-900/30' : 'border-slate-200 bg-white'}`}>
              <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>No stores match your search.</p>
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setPlatformFilter('amazon');
                  setSearchParams({ platform: 'amazon' });
                  setRegionFilter(DEFAULT_STORE_HUB_REGION);
                  setConnectionFilter('all');
                  setAttentionFilter(null);
                }}
                className="mt-3 text-sm text-orange-600 hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-slate-800 bg-slate-900/20' : 'border-slate-200 bg-white'}`}>
              {attentionFilter && (
                <div className={`flex items-center justify-between gap-3 px-4 py-2 text-xs border-b ${isDarkMode ? 'border-slate-800 bg-slate-900/60 text-slate-300' : 'border-slate-100 bg-orange-50/60 text-slate-700'}`}>
                  <span>
                    Showing <strong>{visibleGroups.length}</strong> store{visibleGroups.length === 1 ? '' : 's'} flagged{' '}
                    <strong>{attentionFilter === 'critical' ? 'Act now' : attentionFilter === 'warning' ? 'Watch' : 'Scale up'}</strong>
                  </span>
                  <button type="button" onClick={() => setAttentionFilter(null)} className="font-semibold text-orange-600 hover:underline">
                    Show all stores
                  </button>
                </div>
              )}
              <div className={STORE_LIST_TABLE_MIN_W}>
                <StoreListTableHeader isDarkMode={isDarkMode} />
                <div className="hidden lg:block">
                {listPagination.paginatedItems.map((group) => {
                  const primary = group.primaryStore;
                  const isDisc = !primary?.isConnected;
                  return (
                    <StoreRow
                        key={group.id}
                        store={primary}
                        storeGroup={group}
                        onEnter={handleEnterStore}
                        onReconnect={isDisc && canConnect ? handleReconnect : undefined}
                        onDisconnect={!isDisc && canDisconnect ? handleDisconnect : undefined}
                        disconnecting={disconnectingId === (primary?.profileId || primary?.storeId)}
                        performanceSummaries={perfSummaries}
                        performanceLoading={metricsLoading}
                        isDarkMode={isDarkMode}
                        isAdmin={isAdmin}
                        attention={attention.byGroup.get(group.id)}
                        showRegionHint={showRegionHintInRows}
                    />
                  );
                })}
                </div>
                <div className="lg:hidden space-y-3 p-3">
                  {listPagination.paginatedItems.map((group) => {
                    const primary = group.primaryStore;
                    const isDisc = !primary?.isConnected;
                    return (
                      <StoreRow
                        key={`m-${group.id}`}
                        layout="card"
                        store={primary}
                        storeGroup={group}
                        onEnter={handleEnterStore}
                        onReconnect={isDisc && canConnect ? handleReconnect : undefined}
                        onDisconnect={!isDisc && canDisconnect ? handleDisconnect : undefined}
                        disconnecting={disconnectingId === (primary?.profileId || primary?.storeId)}
                        performanceSummaries={perfSummaries}
                        performanceLoading={metricsLoading}
                        isDarkMode={isDarkMode}
                        isAdmin={isAdmin}
                        showRegionHint={showRegionHintInRows}
                      />
                    );
                  })}
                </div>
              </div>
              <TablePagination
                className={isDarkMode ? '!border-slate-800 !bg-slate-900/50 !text-slate-300' : ''}
                currentPage={listPagination.currentPage}
                totalPages={listPagination.totalPages}
                totalItems={listPagination.totalItems}
                startIndex={listPagination.startIndex}
                endIndex={listPagination.endIndex}
                pageSize={listPagination.pageSize}
                onPageChange={listPagination.setCurrentPage}
                onPageSizeChange={listPagination.setPageSize}
                itemLabel="stores"
              />
              <div className={`px-4 py-2 flex flex-wrap items-center gap-3 border-t ${
                isDarkMode ? 'border-slate-800' : 'border-slate-100'
              }`}>
                <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                  {totalCount} store group{totalCount === 1 ? '' : 's'} loaded
                  {profilesHasMore ? ' (more available)' : ''}
                </p>
                {profilesHasMore && (
                  <button
                    type="button"
                    onClick={() => loadMoreProfiles()}
                    className="text-xs font-semibold text-orange-600 hover:underline"
                  >
                    Load more from account
                  </button>
                )}
              </div>
            </div>
          )}
          </>
          )}
        </main>
    </div>
  );
}

function EmptyState({ isDarkMode, onConnect, connecting, canConnect }) {
  return (
    <div className={`flex flex-col items-center justify-center py-20 rounded-xl border ${
      isDarkMode ? 'border-slate-800 bg-slate-900/30' : 'border-slate-200 bg-white'
    }`}>
      <BuildingStorefrontIcon className={`h-14 w-14 mb-4 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} />
      <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>No stores connected</h3>
      <p className={`text-sm mt-2 max-w-md text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
        {canConnect
          ? 'Connect your first marketplace store to start managing ads.'
          : 'Ask your administrator to assign store access to your account.'}
      </p>
      {canConnect && (
        <button
          type="button"
          onClick={onConnect}
          disabled={connecting}
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
        >
          <PlusIcon className="h-4 w-4" />
          {connecting ? 'Connecting…' : 'Connect store'}
        </button>
      )}
    </div>
  );
}

export default StoreSelection;
